import { useTranslation } from 'react-i18next';
import { Lock } from 'lucide-react';
import { Link } from 'react-router-dom';
import { maskValue, isAlreadyMasked, MASK_CHAR } from '../../../shared/lib/mask';

/**
 * A masked block on the free tier (section 6.2).
 *
 * The critical property is that the real values are **not in this component**.
 * Masking happens on the server, so closed data is never sent to the browser and
 * cannot be recovered from DevTools — which is the whole reason the specification
 * insists on server-side masking. What arrives here is either `null` or an
 * already-masked string such as `***112108**` that the source itself published.
 *
 * This component therefore does not "hide" anything: it renders a shape that
 * tells the user data exists, without having the data. If a real value ever
 * reached it, that would be a server bug, not something a CSS rule could fix.
 *
 * The `░` run is deliberately close to, but not equal to, the real length, and
 * it is seeded so it does not change between renders — a length that flickered
 * would leak the true count to anyone averaging a few repaints.
 */

/** What the server sends for a masked block: structure, never content. */
export interface MaskedBlockHint {
  /** Approximate display lengths, keyed by field name. */
  readonly shape?: Record<string, number>;
  /** Free-form hints the server is willing to reveal (e.g. "3 founders"). */
  readonly counts?: Record<string, number>;
}

export function MaskedBlock({
  blockKey,
  data,
}: {
  blockKey: string;
  data: unknown;
}): React.ReactNode {
  const { t } = useTranslation();

  const hint = (data ?? {}) as MaskedBlockHint;
  const shape = hint.shape ?? {};
  const counts = hint.counts ?? {};

  // What the free tier is allowed to show: how many records exist, never who or
  // how much. Section 13.1 grants exactly the counters, and nothing else.
  const counter = Object.entries(counts);

  return (
    <div className="relative">
      <div className="space-y-2 select-none" aria-hidden>
        {Object.entries(shape).map(([field, length]) => (
          <div key={field} className="flex gap-3">
            <span className="w-32 shrink-0 text-xs text-[var(--color-muted-fg)]">
              {t(`blocks.${blockKey}.${field}`, humanize(field))}
            </span>
            <span className="font-mono text-sm tracking-tight text-[var(--color-muted-fg)]">
              {maskValue('x'.repeat(Math.max(2, length)), `${blockKey}:${field}`)}
            </span>
          </div>
        ))}

        {Object.keys(shape).length === 0 && (
          <p className="font-mono text-sm text-[var(--color-muted-fg)]">
            {MASK_CHAR.repeat(28)}
          </p>
        )}
      </div>

      {counter.length > 0 && (
        <ul className="mt-3 space-y-1 text-xs text-[var(--color-muted-fg)]">
          {counter.map(([field, value]) => (
            <li key={field}>
              {t(`blocks.${blockKey}.${field}`, humanize(field))}:{' '}
              <span className="font-medium text-[var(--color-fg)]">{value}</span>
            </li>
          ))}
        </ul>
      )}

      {/* The paywall itself: an explanation and two honest routes, no dark
          patterns and no countdown. */}
      <div className="mt-4 rounded-[var(--radius-control)] border border-[var(--color-border)] bg-[var(--color-surface-2)] p-3">
        <p className="flex items-center gap-2 text-sm font-medium">
          <Lock aria-hidden className="h-4 w-4 text-[var(--color-muted-fg)]" />
          {t('lookup.maskedTitle')}
        </p>
        <p className="mt-1 text-xs text-[var(--color-muted-fg)]">{t('lookup.maskedBody')}</p>

        <div className="mt-3 flex flex-wrap gap-2 print:hidden">
          <Link
            to="/pricing"
            className="rounded-[var(--radius-control)] bg-brand-700 px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-800"
          >
            {t('lookup.openFor', { price: '0.30' })}
          </Link>
          <Link
            to="/pricing"
            className="rounded-[var(--radius-control)] border border-[var(--color-border)] px-3 py-1.5 text-xs font-medium hover:border-brand-600 hover:text-brand-600"
          >
            {t('lookup.subscribeFrom', { price: '5.00' })}
          </Link>
        </div>
      </div>
    </div>
  );
}

/** A mask the server produced, shown verbatim (e.g. `***112108**`). */
export function ServerMasked({ value }: { value: string }): React.ReactNode {
  return (
    <span className="font-mono text-sm text-[var(--color-muted-fg)]">
      {isAlreadyMasked(value) ? value : maskValue(value, value)}
    </span>
  );
}

function humanize(key: string): string {
  return key
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/^./, (char) => char.toUpperCase());
}
